import CampaignIdentityBlock from '../../components/campaign/CampaignIdentityBlock';
import { useState, useMemo } from 'react';
import { useCampaigns } from '../../hooks/queries/useCampaigns';
import type { Campaign } from '@applicant-reviewer/api';

interface CampaignListViewProps {
  onNavigate?: (path: string) => void;
}

type SortField = 'name' | 'role' | 'status';

export default function CampaignListView({ onNavigate }: CampaignListViewProps) {
  const [sortField, setSortField] = useState<SortField | null>(null);
  const [sortAsc, setSortAsc] = useState<boolean>(true);
  const [showSortMenu, setShowSortMenu] = useState(false);

  const { data: campaigns, isLoading, isError } = useCampaigns();

  const sortedCampaigns = useMemo(() => {
    let sorted = [...(campaigns ?? [])];

    if (sortField) {
      sorted.sort((a, b) => {
        let comparison = 0;

        if (sortField === 'name') {
          comparison = a.name.toLowerCase().localeCompare(b.name.toLowerCase());
        } else if (sortField === 'role') {
          comparison = a.role.toLowerCase().localeCompare(b.role.toLowerCase());
        } else if (sortField === 'status') {
          const statusOrder = { active: 0, draft: 1, suspended: 2 };
          comparison = statusOrder[a.status] - statusOrder[b.status];
        }

        return sortAsc ? comparison : -comparison;
      });
    }

    return sorted;
  }, [campaigns, sortField, sortAsc]);

  const handleRowClick = (campaignId: string) => {
    if (onNavigate) {
      onNavigate(`campaigns/${campaignId}`);
    }
  };

  const getStatusColor = (status: Campaign['status']) => {
    switch (status) {
      case 'active':
        return 'bg-green-500';
      case 'draft':
        return 'bg-gray-400';
      case 'suspended':
        return 'bg-yellow-500';
      default:
        return 'bg-gray-400';
    }
  };

  const formatCurrency = (amount: number) => {
    return `$${amount.toLocaleString()}`;
  };

  const formatGoal = (campaign: Campaign) => {
    if (campaign.goalType === 'perpetual') {
      return 'Perpetual';
    } else if (campaign.goalType === 'date') {
      return campaign.goalValue || '—';
    } else if (campaign.goalType === 'hires') {
      return `${campaign.goalValue} hires`;
    } else if (campaign.goalType === 'budget') {
      return formatCurrency(campaign.goalValue as number);
    }
    return '—';
  };

  return (
    <div className="h-full flex flex-col bg-gray-50">
      {/* Header */}
      <div className="border-b bg-white">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <h1 className="text-[20px] font-bold text-gray-900" style={{ fontFamily: 'Nunito, sans-serif' }}>
            Active Campaigns
          </h1>
        </div>
      </div>

      {/* Table */}
      <div className="flex-1 overflow-auto">
        <div className="max-w-7xl mx-auto px-6 py-6">
          {isLoading && (
            <div className="flex items-center justify-center py-12">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mr-3"></div>
              <span className="text-sm text-gray-500">Loading campaigns...</span>
            </div>
          )}
          {isError && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-sm text-red-700">
              Failed to load campaigns. Please try again.
            </div>
          )}
          {!isLoading && !isError && (
            <div className="bg-white border rounded-lg shadow-sm overflow-hidden">
              <table className="w-full">
                <thead>
                  <tr className="border-b bg-gray-50">
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      Campaign
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      Goal
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      Hires
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      Cost
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider w-20">
                      <div className="flex justify-end relative">
                        <button
                          type="button"
                          className="p-1.5 rounded hover:bg-gray-100"
                          title="Sort"
                          onClick={() => setShowSortMenu(v => !v)}
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" className="w-6 h-6 text-gray-700" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M8 17V7" />
                            <path d="M8 7 L6 9" />
                            <path d="M8 7 L10 9" />
                            <path d="M16 7V17" />
                            <path d="M16 17 L14 15" />
                            <path d="M16 17 L18 15" />
                          </svg>
                        </button>
                        {showSortMenu && (
                          <div className="absolute right-0 top-10 w-56 bg-white border rounded shadow-lg p-2 z-10">
                            <div className="text-xs text-gray-500 px-1 pb-1">Sort by</div>
                            <div className="divide-y">
                              <button className="w-full text-left px-2 py-2 text-sm hover:bg-gray-50 rounded" onClick={() => { setSortField('name'); setSortAsc(true); setShowSortMenu(false); }}>Campaign (A → Z)</button>
                              <button className="w-full text-left px-2 py-2 text-sm hover:bg-gray-50 rounded" onClick={() => { setSortField('name'); setSortAsc(false); setShowSortMenu(false); }}>Campaign (Z → A)</button>
                              <button className="w-full text-left px-2 py-2 text-sm hover:bg-gray-50 rounded" onClick={() => { setSortField('role'); setSortAsc(true); setShowSortMenu(false); }}>Role (A → Z)</button>
                              <button className="w-full text-left px-2 py-2 text-sm hover:bg-gray-50 rounded" onClick={() => { setSortField('role'); setSortAsc(false); setShowSortMenu(false); }}>Role (Z → A)</button>
                              <button className="w-full text-left px-2 py-2 text-sm hover:bg-gray-50 rounded" onClick={() => { setSortField('status'); setSortAsc(true); setShowSortMenu(false); }}>Status (Active First)</button>
                              <button className="w-full text-left px-2 py-2 text-sm hover:bg-gray-50 rounded" onClick={() => { setSortField('status'); setSortAsc(false); setShowSortMenu(false); }}>Status (Draft First)</button>
                            </div>
                          </div>
                        )}
                      </div>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {sortedCampaigns.length === 0 ? (
                    <tr>
                      <td colSpan={5} className="px-4 py-8 text-center text-sm text-gray-500">
                        No campaigns found.
                      </td>
                    </tr>
                  ) : (
                    sortedCampaigns.map((campaign) => (
                      <tr
                        key={campaign.campaignId}
                        onClick={() => handleRowClick(campaign.campaignId)}
                        className="border-b last:border-b-0 hover:bg-gray-50 cursor-pointer transition-colors"
                      >
                        {/* Campaign Identity with Status Circle */}
                        <td className="px-4 py-4">
                          <div className="flex items-center gap-[17px]">
                            <div className={`w-3 h-3 rounded-full flex-shrink-0 ${getStatusColor(campaign.status)}`} />
                            <CampaignIdentityBlock
                              campaignName={campaign.name}
                              role={campaign.role}
                              locations={campaign.location}
                            />
                          </div>
                        </td>

                        {/* Goal */}
                        <td className="px-4 py-4">
                          <span className="text-sm text-gray-900">{formatGoal(campaign)}</span>
                        </td>

                        {/* Hires */}
                        <td className="px-4 py-4 text-right">
                          <span className="text-sm font-medium text-gray-900">{campaign.hires}</span>
                        </td>

                        {/* Cost */}
                        <td className="px-4 py-4">
                          <span className="text-sm text-gray-700">
                            {formatCurrency(campaign.spend)}
                          </span>
                        </td>

                        {/* Empty cell for sort menu alignment */}
                        <td className="px-4 py-4">
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
